import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { useHistory, useRouteMatch } from "react-router";
import { Card, Input, Row, Space, Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import { TableProps } from "antd/es/table";
import omit from "lodash/omit";
import get from "lodash/get";
import AddButton from "../../../../components/AddButton";
import useAppAuth0 from "../../../../hooks/useAppAuth0";
import styles from "./index.module.scss";
import {
  PromotionDiscountType,
  PromotionListModel,
  PromotionModel,
  PromotionStatus,
} from "../../../../types/model/promotion";
import ResizableTitle from "../../../../components/ResizableTitle";
import { getTotalDisplay } from "../../../../utils/getTotalDisplay";
import * as eComPromotionAPI from "../../../../services/promotion";
import { getOffset } from "../../../../utils/getPagination";
import getPromotionTableColumns from "./getPromotionTableColumns";
import { PromotionSource } from "../components/PromotionForm";
import DropdownInput from "../../../../components/DropdownInput";
import { PromotionListRequest } from "../../../../types/dto/request/promotion";
import useResizableTableHeader from "../../../../hooks/useResizableTableHeader";

const statusOptions = [
  { label: "Redeemable", value: PromotionStatus.REDEEMABLE },
  { label: "Not started", value: PromotionStatus.NOT_STARTED },
  { label: "Expired", value: PromotionStatus.EXPIRED },
  { label: "Maxed out", value: PromotionStatus.MAXED_OUT },
];

function PromotionList() {
  const { user } = useAppAuth0();
  const [query, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 10),
    searchText: withDefault(StringParam, undefined),
    status: withDefault(ArrayParam, undefined),
    promotionType: withDefault(ArrayParam, undefined),
    discountType: withDefault(ArrayParam, undefined),
    sortKey: withDefault(StringParam, undefined),
    sortOrder: withDefault(StringParam, undefined),
  });
  const history = useHistory();
  const { path } = useRouteMatch();
  const columnsToDisplay = useResizableTableHeader<PromotionModel>(getPromotionTableColumns(), 125, 500);
  const [searchText, setSearchText] = useState(query.searchText);
  const [isPromotionListPending, setIsPromotionListPending] = useState(false);
  const [promotionList, setPromotionList] = useState<PromotionListModel>({} as PromotionListModel);
  const [selectStatus, setSelectStatus] = useState<PromotionStatus[]>([]);

  const refreshPromotionList = useCallback(() => {
    setIsPromotionListPending(true);
    eComPromotionAPI
      .fetchPromotionList({
        ...omit(query, ["page"]),
        offset: getOffset(query.page, query.limit),
      } as PromotionListRequest)
      .then((res) => {
        setPromotionList(res);
      })
      .catch(() => {})
      .finally(() => {
        setIsPromotionListPending(false);
      });
  }, [query]);

  useEffect(() => {
    refreshPromotionList();
    setSearchText(query.searchText);
  }, [query, refreshPromotionList]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleInputPressEnter = () => {
    setQuery({
      ...query,
      searchText: searchText?.trim(),
      page: searchText?.trim() ? 1 : query.page,
    });
  };

  const handleOnRowClick: TableProps<PromotionModel>["onRow"] = (record) => ({
    onClick: () =>
      history.push({
        pathname: `${path}/${record.id}`,
        search: `source=${
          record.discountType === PromotionDiscountType.BUY_ONE_GIFT_ONE
            ? PromotionSource.BUY_ONE_GIFT_ONE
            : PromotionSource.RECURLY
        }`,
      }),
  });

  const handleStatusChange = (statuses: PromotionStatus[]) => {
    setSelectStatus(statuses);
    setQuery({ ...query, status: statuses, page: 1 });
  };

  const handleTableChange: TableProps<PromotionModel>["onChange"] = (pagination, filters, sorter, extra) => {
    const { order, columnKey } = sorter as SorterResult<PromotionModel>;
    const isPaginateAction = get(extra, "action") === "paginate";
    setQuery({
      ...query,
      page: isPaginateAction ? pagination.current : 1,
      limit: pagination.pageSize,
      promotionType: get(filters, "promotionType", []) as string[],
      discountType: get(filters, "discountType", []) as string[],
      sortKey: columnKey as string,
      sortOrder: order as string,
    });
  };

  return (
    <Card
      title="Promotion list"
      extra={user.canCreatePromotion && <AddButton text={"+ Add new Promotion"} />}
      className="page-container"
    >
      <Row justify={"space-between"} className={styles.filter}>
        <Space>
          <DropdownInput
            options={statusOptions}
            placeholder="All statuses"
            value={selectStatus}
            onChange={(statuses) => handleStatusChange(statuses as PromotionStatus[])}
          />
          <Input
            allowClear
            value={searchText}
            onChange={handleInputChange}
            onBlur={() => setSearchText((prevState) => prevState?.trim())}
            onPressEnter={handleInputPressEnter}
            className={styles.promotionNameInput}
            prefix={<SearchOutlined />}
            placeholder="Search promotion code or applied base price"
          />
        </Space>
      </Row>

      <Table<PromotionModel>
        loading={{
          spinning: isPromotionListPending,
        }}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        scroll={{ scrollToFirstRowOnChange: true, x: "100%", y: "max-content" }}
        rowKey={(record) => record.id}
        columns={columnsToDisplay}
        dataSource={promotionList.list}
        pagination={{
          showTotal: getTotalDisplay,
          current: query.page,
          pageSize: query.limit,
          total: promotionList.total,
          defaultCurrent: 1,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setQuery({
              ...query,
              page,
              limit: pageSize,
            });
          },
        }}
        onChange={handleTableChange}
        onRow={handleOnRowClick}
      />
    </Card>
  );
}

export default PromotionList;
